// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';

/**
 * Frequencies whose period boundaries are anchored to a day-of-month. When
 * Luxon's month-arithmetic clamps (e.g., Jan 31 + 1 month = Feb 28 because
 * Feb has no 31st), the clamped date belongs to the CURRENT period instead
 * of starting a new one. DAILY/WEEKLY/BIWEEKLY never clamp in this sense.
 */
export const MONTH_ANCHORED_FREQUENCIES: ReadonlySet<string> = new Set([
  'MONTHLY',
  'QUARTERLY',
  'SEMIANNUALLY',
  'ANNUALLY',
  'BIENNIALLY',
  'TRIENNIALLY',
  'QUADRENNIALLY',
  'QUINQUENNIALLY',
]);

/**
 * Advances a DateTime by one period of the given frequency.
 * This is the single source of truth for frequency-based date arithmetic —
 * used by both charge generation and period counting.
 */
export function advanceByFrequency(dt: DateTime, frequency: string): DateTime {
  switch (frequency.toUpperCase()) {
    case 'DAILY':
      return dt.plus({ days: 1 });
    case 'WEEKLY':
      return dt.plus({ weeks: 1 });
    case 'BIWEEKLY':
      return dt.plus({ weeks: 2 });
    case 'MONTHLY':
      return dt.plus({ months: 1 });
    case 'QUARTERLY':
      return dt.plus({ months: 3 });
    case 'SEMIANNUALLY':
      return dt.plus({ months: 6 });
    case 'ANNUALLY':
      return dt.plus({ years: 1 });
    case 'BIENNIALLY':
      return dt.plus({ years: 2 });
    case 'TRIENNIALLY':
      return dt.plus({ years: 3 });
    case 'QUADRENNIALLY':
      return dt.plus({ years: 4 });
    case 'QUINQUENNIALLY':
      return dt.plus({ years: 5 });
    case 'ONCE':
      return dt.plus({ years: 100 });
    default:
      return dt.plus({ months: 1 });
  }
}

/**
 * Counts periods by walking forward from start to end, inclusive of both
 * endpoints. Uses the same algorithm as charge generation to ensure the
 * count always matches the number of charges produced.
 */
export function countPeriods(
  start: DateTime,
  end: DateTime,
  frequency: string
): number {
  if (end < start) return 0;

  const freq = frequency.toUpperCase();
  if (freq === 'ONCE') return 1;

  const monthAnchored = MONTH_ANCHORED_FREQUENCIES.has(freq);
  let count = 0;
  let current = start;
  while (current <= end) {
    const next = advanceByFrequency(current, freq);
    const clamped = monthAnchored && next.day !== current.day;
    const naturalEnd = clamped ? next : next.minus({ days: 1 });
    const periodEnd = naturalEnd > end ? end : naturalEnd;
    count++;
    current = periodEnd.plus({ days: 1 });
  }
  return count;
}
